import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

// Declare grecaptcha for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      render: (container: string | HTMLElement, parameters: {
        sitekey: string;
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => number;
      reset: (widgetId?: number) => void;
    };
    handleRecaptchaChange?: (token: string) => void;
  }
}

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    budget: '',
    message: '',
    services: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);

  // Google reCAPTCHA v2 Site Key
  const RECAPTCHA_SITE_KEY = '6LcNEEIsAAAAAM8U7Y3Zs3Yvlij_Vmg7r7EIH57K';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleRecaptchaChange = (token: string | null) => {
    if (token) {
      setRecaptchaToken(token);
      setRecaptchaError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setRecaptchaError('Please complete the reCAPTCHA verification');
      return;
    }
    
    setRecaptchaError('');
    setIsSubmitting(true);
    
    try {
      const formPayload = new URLSearchParams();
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('company', formData.company);
      formPayload.append('phone', formData.phone);
      formPayload.append('industry', formData.industry);
      formPayload.append('budget', formData.budget);
      formPayload.append('services', formData.services.join(', '));
      formPayload.append('message', formData.message);
      formPayload.append('g-recaptcha-response', recaptchaToken);

      const response = await fetch('https://readdy.ai/api/form/8z1v7j47c8nh3vn51szm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formPayload.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          industry: '',
          budget: '',
          message: '',
          services: []
        });
        setRecaptchaToken('');
        // Reset reCAPTCHA
        if (window.grecaptcha && recaptchaWidgetId !== null) {
          window.grecaptcha.reset(recaptchaWidgetId);
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize reCAPTCHA when component mounts
  React.useEffect(() => {
    // Define the callback function globally
    window.handleRecaptchaChange = (token: string) => {
      handleRecaptchaChange(token);
    };

    const loadRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        try {
          const widgetId = window.grecaptcha.render('recaptcha-container', {
            sitekey: RECAPTCHA_SITE_KEY,
            callback: 'handleRecaptchaChange',
            'expired-callback': () => {
              setRecaptchaToken('');
              setRecaptchaError('reCAPTCHA expired. Please verify again.');
            },
            'error-callback': () => {
              setRecaptchaError('reCAPTCHA error. Please try again.');
            }
          });
          setRecaptchaWidgetId(widgetId);
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    };

    // Check if script is already loaded
    if (window.grecaptcha && window.grecaptcha.render) {
      loadRecaptcha();
    } else {
      // Wait for script to load
      const checkRecaptcha = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkRecaptcha);
          loadRecaptcha();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkRecaptcha), 10000);
    }

    return () => {
      // Cleanup
      if (window.handleRecaptchaChange) {
        delete window.handleRecaptchaChange;
      }
    };
  }, []);

  const services = [
    'SEO Optimization',
    'SEM & PPC Management',
    'Meta Advertising',
    'Google Ads',
    'AI Software Solutions',
    'Lead Generation',
    'Analytics & Reporting',
    'Landing Page Optimization'
  ];

  const industries = [
    'Mortgage Refinance',
    'Debt Consolidation',
    'Home Security',
    'Financial Services',
    'Insurance',
    'Real Estate',
    'B2B Software',
    'Other'
  ];

  const budgetRanges = [
    '$5,000 - $10,000/month',
    '$10,000 - $25,000/month',
    '$25,000 - $50,000/month',
    '$50,000 - $100,000/month',
    '$100,000+/month'
  ];

  const contactInfo = [
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      details: 'info@halcomanalytics.com',
      href: 'mailto:info@halcomanalytics.com',
      subtitle: 'Get a response within 24 hours'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      details: '+91 0120 418 7944',
      href: 'tel:+910120418794',
      subtitle: 'Monday - Friday, 9AM - 6PM IST'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Visit Us',
      details: 'Head Office: Suite 304, H 159, VDS, Sector 63, Noida, 201301',
      subtitle: 'Schedule an in-person consultation'
    },
    {
      icon: 'ri-time-line',
      title: 'Response Time',
      details: '< 24 Hours',
      subtitle: 'We prioritize quick responses'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.8), rgba(30, 64, 175, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20meeting%20in%20modern%20office%20with%20team%20discussing%20lead%20generation%20strategies%2C%20laptops%20and%20documents%20on%20conference%20table%2C%20bright%20corporate%20environment&width=1920&height=800&seq=contact-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Let's Grow Your Business Together
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              Ready to generate more qualified leads and scale your international business? Get in touch with our experts for a free consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white p-6 md:p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                  <i className={`${info.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{info.title}</h3>
                {(info as any).href ? (
                  <>
                    <a href={(info as any).href} className="text-lg text-blue-600 font-semibold mb-2 hover:text-blue-800 transition-colors block">{info.details}</a>
                    <p className="text-sm text-gray-600">{info.subtitle}</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg text-blue-600 font-semibold mb-2">{info.details}</p>
                    <p className="text-sm text-gray-600">{info.subtitle}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Your Free Strategy Consultation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fill out the form below and our lead generation experts will analyze your business and provide personalized recommendations.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-gray-50 p-8 md:p-12 rounded-2xl shadow-lg" data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  placeholder="Enter your company name"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-707 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry *
                </label>
                <select
                  id="industry"
                  name="industry"
                  required
                  value={formData.industry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm bg-white"
                >
                  <option value="">Select your industry</option>
                  {industries.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Budget Range
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm bg-white"
                >
                  <option value="">Select budget range</option>
                  {budgetRanges.map((range) => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-4">
                Services of Interest (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {services.map((service) => (
                  <label key={service} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleServiceChange(service)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600"
                    />
                    <span className="ml-2 text-sm text-gray-700">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Project Details & Goals *
              </label>
              <textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleInputChange}
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-600 focus:outline-none text-sm resize-none"
                placeholder="Tell us about your current lead generation challenges and goals..."
              ></textarea>
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.message.length}/500 characters
              </div>
            </div>

            {/* Google reCAPTCHA Section */}
            <div className="mb-8">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Security Verification *
              </label>
              <div id="recaptcha-container"></div>
              {recaptchaError && (
                <div className="mt-3 flex items-center text-red-600">
                  <i className="ri-error-warning-line mr-2"></i>
                  <span className="text-sm font-medium">{recaptchaError}</span>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3">
                This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-check-circle-line mr-2"></i>
                  Thank you! We've received your message and will respond within 24 hours.
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  There was an error submitting your form. Please try again or contact us directly.
                </div>
              </div>
            )}

            <Button 
              type="submit" 
              size="lg" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line mr-2"></i>
                  Sending Message...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-2"></i>
                  Send Message & Get Free Consultation
                </>
              )}
            </Button>

            <p className="text-sm text-gray-600 text-center mt-4">
              By submitting this form, you agree to our privacy policy. We respect your privacy and will never share your information.
            </p>
          </form>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Visit Our Office
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Located in Noida, India, we&apos;re always ready to meet with clients and discuss your lead generation goals in person.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.990824376473!2d77.3816496!3d28.6300371!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cef49ad56d8c3%3A0x64f4675f232ed5e3!2sTrimala%20Ventures!5e0!3m2!1sen!2sin!4v1758830874289!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Halcom Analytics Office Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our lead generation services and process.
            </p>
          </div>

          <div className="space-y-8">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">How quickly can we expect to see results?</h3>
              <p className="text-gray-600 leading-relaxed">
                While SEO results typically take 3-6 months to fully materialize, our PPC and social media campaigns can start generating qualified leads within the first 30 days. We provide detailed reporting so you can track progress from day one.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What makes Halcom Analytics different from other agencies?</h3>
              <p className="text-gray-600 leading-relaxed">
                Our specialization in high-value industries like mortgage, debt consolidation, and home security, combined with our international expertise and AI-powered solutions, sets us apart. We focus on quality over quantity and have a proven track record of generating $500M+ in client revenue.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Do you work with companies outside the US?</h3>
              <p className="text-gray-600 leading-relaxed">
                Yes! We specialize in international lead generation and work with clients across North America, Europe, Australia, and other English-speaking markets. Our strategies are tailored for each region&apos;s unique market conditions.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What&apos;s included in your reporting and analytics?</h3>
              <p className="text-gray-600 leading-relaxed">
                We provide comprehensive monthly reports including lead volume, cost per lead, conversion rates, ROI analysis, and campaign performance across all channels. You&apos;ll also have access to real-time dashboards and weekly check-ins with your dedicated account manager.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Scale Your Lead Generation?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join 150+ successful international clients who trust Halcom Analytics to drive their business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-black hover:bg-gray-100"
            >
              <i className="ri-arrow-up-line mr-2 text-black"></i>
              <span className="text-black">Fill Out Form Above</span>
            </Button>
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-black hover:bg-gray-100"
            >
              <i className="ri-calendar-line mr-2 text-black"></i>
              <span className="text-black">Schedule Consultation</span>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
